import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { getAuthToken, isTokenExpired } from 'src/consumer/services/localstorageServices'

import AuthenticatedTopBar from './AuthenticatedTopBar'
import { ChevronDown, LogIn } from './icons'
import { CAREGIVER_NAV, DOCTOR_NAV } from './roleData'
import { Container } from './sections'

// Light auth-state hook for the marketing top bar. The marketing layout
// (AumhumSite/Shell) is rendered for both anonymous and signed-in users;
// the original TopBar always rendered the "Login" button, which made the
// /care/* flow look broken to a user who had just signed up via the
// patient-prospect OTP step (token in localStorage, but the header still
// invited them to sign in).
//
// We poll a couple of cheap signals so the bar reacts even when the
// localStorage write happened in the same tab:
//   - the `storage` event (other tabs)
//   - a custom `aumhum:auth-changed` event we dispatch on login/logout
//   - on every route change (covers SPA navigation post-signup)
function useIsAuthenticated() {
  const location = useLocation()
  const [authed, setAuthed] = useState(() => {
    try {
      return !!getAuthToken() && !isTokenExpired()
    } catch {
      return false
    }
  })
  useEffect(() => {
    const refresh = () => {
      try {
        setAuthed(!!getAuthToken() && !isTokenExpired())
      } catch {
        setAuthed(false)
      }
    }
    refresh()
    window.addEventListener('storage', refresh)
    window.addEventListener('aumhum:auth-changed', refresh)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener('aumhum:auth-changed', refresh)
    }
  }, [location.pathname])
  return authed
}

// ── NavDropdown ──────────────────────────────────────────
// Small hover+click dropdown used in the top bar for Doctors / Care-Givers.

const NavDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div
      className="as-dd"
      data-open={open}
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="as-dd-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown />
      </button>
      <div className="as-dd-panel" role="menu">
        <div className="as-dd-card">
          {items.map(({ to, label: l, sub }) => (
            <Link
              key={to}
              to={to}
              role="menuitem"
              className="as-dd-item"
              onClick={() => setOpen(false)}
            >
              <div className="as-dd-item-label">{l}</div>
              {sub ? <div className="as-dd-item-sub">{sub}</div> : null}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── TopBar ───────────────────────────────────────────────

// Always-floating header. We track whether the page has been scrolled past
// a small threshold so we can render an elevated, condensed variant of the
// header (more blur + shadow) once the user has started scrolling. The
// header itself never hides — it floats over content in both directions.
function useScrolledPastThreshold({ threshold = 8 } = {}) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    let rafId = 0
    let queued = false

    const update = () => {
      queued = false
      setScrolled((window.scrollY || 0) > threshold)
    }

    const onScroll = () => {
      if (queued) return
      queued = true
      rafId = window.requestAnimationFrame(update)
    }

    // Sync once on mount (handles SSR hydrate + anchor scroll restores).
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [threshold])
  return scrolled
}

export const TopBar = ({ onLogin, contact, onContactClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const scrolled = useScrolledPastThreshold()
  const location = useLocation()
  const authed = useIsAuthenticated()
  // Lets a signed-in user temporarily render the public/anonymous nav
  // (Doctors / Care-Givers / Self-care / …) without logging out — they
  // click "Browse public site" inside their profile dropdown. Persists
  // for the current SPA session; route changes reset it so the
  // authenticated chrome comes back the next time they land on a
  // signed-in surface.
  const [publicNavOverride, setPublicNavOverride] = useState(false)
  useEffect(() => {
    setPublicNavOverride(false)
  }, [location.pathname])

  // Close the mobile drawer whenever the route changes. Relying on the
  // route change (rather than a blanket onClick on the <nav>) means that
  // tapping inside <details>/<summary> to expand a dropdown doesn't
  // accidentally dismiss the drawer, and Link navigation isn't racing
  // against its own parent unmount.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Authenticated users see a different header — context-aware nav,
  // a profile chip, and (for multi-role accounts) a portal switcher.
  // See AuthenticatedTopBar for the rationale + role → portal mapping.
  // Branched AFTER all hooks above so the call order stays stable
  // across renders (React Hooks rule).
  if (authed && !publicNavOverride) {
    return (
      <AuthenticatedTopBar
        contact={contact}
        onContactClick={onContactClick}
        scrolled={scrolled}
        onSwitchToPublicNav={() => setPublicNavOverride(true)}
      />
    )
  }

  return (
    <header
      className="as-topbar"
      role="banner"
      data-scrolled={scrolled ? 'true' : 'false'}
    >
      <Container>
        <div className="as-topbar-inner">
          <Link to="/" className="as-brand" aria-label="AUMHUM — home">
            <span className="as-brand-mark" aria-hidden="true">
              <img
                src={APP_CONSTANTS.APP_LOGO_LINK}
                alt=""
                loading="eager"
                decoding="async"
                width={32}
                height={32}
              />
            </span>
            <span>AUMHUM</span>
          </Link>
          <nav className="as-nav" aria-label="Primary">
            <NavDropdown label="Doctors" items={DOCTOR_NAV} />
            <NavDropdown label="Care-Givers" items={CAREGIVER_NAV} />
            <Link to="/self-care">Self-care</Link>
            <Link to="/students">Students</Link>
            <Link to="/aumhum-for-workplace">For Organizations</Link>
          </nav>
          <div className="as-topbar-actions">
            {contact ? (
              <button
                type="button"
                className="as-header-phone"
                onClick={onContactClick}
                aria-label={`Call AUMHUM at ${contact.display}`}
                title="Connect with our team"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="as-header-phone-num">{contact.display}</span>
              </button>
            ) : null}
            {authed ? (
              <Link
                to="/care/status"
                className="as-login-btn as-care-link-btn"
                aria-label="My care"
              >
                My care
              </Link>
            ) : (
              <button
                type="button"
                className="as-login-btn as-header-login-btn as-header-login-btn-mobile"
                onClick={onLogin}
                aria-label="Login"
                title="Login"
              >
                <LogIn className="as-login-btn-icon" />
                <span>Login</span>
              </button>
            )}
            <Link
              to={authed ? '/care/status' : '/care/start'}
              className="as-btn as-btn-secondary as-cta-desktop as-care-cta-link"
              aria-label={authed ? 'Continue care' : 'For patients'}
            >
              {authed ? 'Continue care' : 'For patients'}
            </Link>
            {!authed ? (
              <button
                type="button"
                className="as-login-btn as-header-login-btn as-header-login-btn-desktop"
                onClick={onLogin}
                aria-label="Login"
                title="Login"
              >
                <LogIn className="as-login-btn-icon" />
                <span>Login</span>
              </button>
            ) : null}
            <button
              type="button"
              className="as-mobile-toggle as-login-btn"
              aria-expanded={mobileOpen}
              aria-controls="aumhum-mobile-menu"
              aria-label="Toggle navigation"
              onClick={() => setMobileOpen((m) => !m)}
            >
              Menu
            </button>
          </div>
        </div>
        {mobileOpen ? (
          <nav
            id="aumhum-mobile-menu"
            className="as-mobile-menu"
            aria-label="Mobile"
            onClick={(e) => {
              // Close the drawer only when the user actually tapped a
              // navigation link. A tap on <summary> must toggle <details>
              // open, and a tap on empty space shouldn't dismiss either —
              // the previous blanket handler made dropdowns unusable on
              // mobile because every tap collapsed the drawer mid-frame.
              if (e.target.closest && e.target.closest('a')) {
                setMobileOpen(false)
              }
            }}
          >
            <details>
              <summary>Doctors</summary>
              <div>
                {DOCTOR_NAV.map((d) => (
                  <Link key={d.to} to={d.to}>
                    {d.label}
                  </Link>
                ))}
              </div>
            </details>
            <details>
              <summary>Care-Givers</summary>
              <div>
                {CAREGIVER_NAV.map((c) => (
                  <Link key={c.to} to={c.to}>
                    {c.label}
                  </Link>
                ))}
              </div>
            </details>
            <Link to="/self-care">Self-care</Link>
            <Link to="/students">Students</Link>
            <Link to="/aumhum-for-workplace">For Organizations</Link>
            <Link to="/aumhum-for-clinics">For Clinics &amp; Rehab</Link>
            <Link to="/join">Join our network</Link>
          </nav>
        ) : null}
      </Container>
    </header>
  )
}

// ── Footer ───────────────────────────────────────────────

// AUMHUM's social channels. Kept in sync with the canonical list in
// the legacy PublicAppFooter (src/consumer/components/PublicAppFooter/
// index.jsx) so both footers point at the same handles.
export const SOCIAL_LINKS = [
  {
    id: 'youtube',
    label: 'YouTube',
    href: 'https://www.youtube.com/@aumhumApp',
    // Brand colour used for hover accent.
    accent: '#ff0033',
    Glyph: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...p}>
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.017 3.017 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    id: 'instagram',
    label: 'Instagram',
    href: 'https://www.instagram.com/aumhum.app/',
    accent: '#e1306c',
    Glyph: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
           strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/aumhum',
    accent: '#0a66c2',
    Glyph: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...p}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    id: 'x',
    label: 'X (Twitter)',
    href: 'https://x.com/aumhums',
    accent: '#111827',
    Glyph: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...p}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
]

export const Footer = () => (
  <footer className="as-footer" role="contentinfo">
    <Container>
      <div className="as-footer-grid">
        <div>
          <div className="as-brand">
            <span className="as-brand-mark" aria-hidden="true">
              <img
                src={APP_CONSTANTS.APP_LOGO_LINK}
                alt=""
                loading="lazy"
                decoding="async"
                width={32}
                height={32}
              />
            </span>
            <span>AUMHUM</span>
          </div>
          <p className="as-p as-mt-3">
            Clinical mental healthcare with integrated psychiatric and endocrine care,
            delivered as a continuous care system.
          </p>

          {/* Social channels. Same handles as the legacy PublicAppFooter. */}
          <ul className="as-social-row" aria-label="AUMHUM on social media">
            {SOCIAL_LINKS.map((s) => (
              <li key={s.id}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="as-social-btn"
                  style={{ '--as-social-accent': s.accent }}
                  aria-label={`AUMHUM on ${s.label}`}
                  title={s.label}
                >
                  <s.Glyph />
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4>Care</h4>
          <ul>
            <li><Link to="/care/start">Get help</Link></li>
            <li><Link to="/how-care-works">How care works</Link></li>
            <li><Link to="/book-consultation">Book a consultation</Link></li>
            <li><Link to="/self-care">Self-care app</Link></li>
          </ul>
        </div>
        <div>
          <h4>Explore</h4>
          <ul>
            <li><Link to="/assessment">Self-assessments</Link></li>
            <li><Link to="/podcast">AUMHUM podcast</Link></li>
            <li>
              <a
                href="https://www.youtube.com/@aumhumApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                YouTube channel
              </a>
            </li>
            <li><Link to="/article">Articles</Link></li>
          </ul>
        </div>
        <div>
          <h4>Clinicians &amp; company</h4>
          <ul>
            <li><Link to="/doctors/psychiatrist">Doctors</Link></li>
            <li><Link to="/care-givers/clinical-psychologist">Care-Givers</Link></li>
            <li><Link to="/students">Students — Experience Program</Link></li>
            <li><Link to="/join">Join our network</Link></li>
            <li><Link to="/aumhum-for-workplace">For organizations</Link></li>
            <li><Link to="/aumhum-for-clinics">For clinics &amp; rehab</Link></li>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/privacy">Privacy</Link></li>
            <li><Link to="/terms">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="as-footer-bottom">
        <p>© {new Date().getFullYear()} AUMHUM. Clinical mental healthcare.</p>
        <p>
          Care is delivered by licensed clinicians in accordance with applicable regulations.
        </p>
      </div>
    </Container>
  </footer>
)
